import { isEditPublisher } from '../../state/publishers/types';
import type { Publisher, YammerState } from '../../state/types';
import { findByIdThrows as findMessageThrows } from '../messages/selectors';
import { isThreadStarterForId, isUserMediaPostThread } from '../threads/selectors';

import { findById } from './findPublisherSelectors';
import { getSharedMessageId } from './sharedThreadSelectors';

const MEDIA_POST_MAX_NUMBER_OF_ATTACHMENTS = 1;
const MEDIA_POST_SUPPORTED_ATTACHMENTS_TYPE = ['imageFiles', 'videoFiles'];

type IsWithinMediaPostAttachmentLimit = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isWithinMediaPostAttachmentLimit: IsWithinMediaPostAttachmentLimit = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  return publisher.attachments.length <= MEDIA_POST_MAX_NUMBER_OF_ATTACHMENTS;
};

type IsAttachmentMediaPostEligible = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isAttachmentMediaPostEligible: IsAttachmentMediaPostEligible = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  const withinAttachmentsLimit = isWithinMediaPostAttachmentLimit(state, publisherId);
  const hasAttachments = publisher.attachments.length > 0;
  const onlySupportedSchema = publisher.attachments.every(({ schema }) =>
    MEDIA_POST_SUPPORTED_ATTACHMENTS_TYPE.includes(schema)
  );

  return hasAttachments && withinAttachmentsLimit && onlySupportedSchema;
};

type IsMediaPost = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isMediaPost: IsMediaPost = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return false;
  }

  const isSharedMessage = getSharedMessageId(state, publisherId);
  if (isSharedMessage) {
    return false;
  }

  const isDiscussionPost = publisher.postType === 'UserNewUpdate' || publisher.postType === 'UserNewMedia';
  const hasMediaPostEligibleAttachment = isAttachmentMediaPostEligible(state, publisherId);

  return isDiscussionPost && hasMediaPostEligibleAttachment;
};

type IsEditMediaPostPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditMediaPostPublisher: IsEditMediaPostPublisher = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher || !isEditPublisher(publisher)) {
    return false;
  }

  const message = findMessageThrows(state, publisher.messageId);
  const { threadId } = message;

  return isThreadStarterForId(state, publisher.messageId) && isUserMediaPostThread(state, threadId);
};
